import { SplitTextField, Typography } from "@suraasa/placebo-ui"
import api from "api"
import { PhoneNumber } from "api/resources/users/types"
import { getNavigationPortal } from "features/Onboarding/Navigation/utils"
import { FormPageProps } from "features/Onboarding/types"
import { Controller, useForm } from "react-hook-form"
import countryCodes from "utils/countryCodes"
import { mapErrorsV2 } from "utils/helpers"
import useCurrentCountry from "utils/hooks/useCurrentCountry"

const PhoneNumberScreen = ({
  data,
  updateData,
  onNext,
  ...props
}: FormPageProps) => {
  const country = useCurrentCountry()

  const defaultDialCode =
    countryCodes.find(item => item.code === country?.isoCode)?.dialCode || "+91"

  const {
    control,
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
  } = useForm<{ phoneNumber: PhoneNumber }>({
    defaultValues: {
      phoneNumber: data.phoneNumber || {
        code: defaultDialCode,
        number: "",
      },
    },
    mode: "onChange",
  })

  const countryCodeOptions = countryCodes.map(country => ({
    label: country.dialCode,
    value: country.dialCode,
  }))

  const updateBackend = async (phoneNumber: PhoneNumber) => {
    try {
      await props.apiAdapter.savePhoneNumber(
        `${phoneNumber.code}${phoneNumber.number}`
      )
    } catch (e) {
      mapErrorsV2(e)
    }
  }

  const onSubmit = handleSubmit(async ({ phoneNumber }) => {
    if (!phoneNumber.code || !phoneNumber.number) return

    try {
      updateData({ phoneNumber })
      await updateBackend(phoneNumber)
      await onNext()
    } catch (error) {
      console.error("Error updating phone number:", error)
    }
  })

  return (
    <div className="flex flex-col items-start">
      <Typography variant="title2" color="onSurface.900">
        What is your phone number?
      </Typography>
      <div className="mt-6"></div>

      <form onSubmit={onSubmit} className="w-full max-w-[380px]">
        <Controller
          control={control}
          name="phoneNumber"
          render={({ field }) => (
            <SplitTextField
              fullWidth
              error={Boolean(errors.phoneNumber)}
              helperText={
                errors.phoneNumber?.number?.message ||
                errors.phoneNumber?.code?.message ||
                errors.phoneNumber?.message
              }
              selectProps={{
                placeholder: "+91",
                options: countryCodeOptions,
                value: countryCodeOptions.find(
                  c => c.value === field.value.code
                ),
                onChange: val => {
                  if (val)
                    field.onChange({
                      ...field.value,
                      code: val.value,
                    })
                },
              }}
              textFieldProps={{
                autoFocus: true,
                type: "number",
                label: "Phone Number",
                placeholder: "Enter phone number",
                value: field.value.number ?? undefined,
                onChange: val => {
                  field.onChange({
                    ...field.value,
                    number: val.target.value,
                  })
                },
              }}
            />
          )}
          rules={{
            validate: v => {
              if (!v) return "Phone number is required"
              if (!v.code) return "Country code is required"
              if (!v.number) return "Phone number is required"
              if (v.number.length < 7)
                return "Phone number must be at least 7 digits"
              if (v.number.length > 15)
                return "Phone number must be at most 15 digits"
              return true
            },
          }}
        />
      </form>

      {getNavigationPortal({
        disableNext: !isValid || isSubmitting,
        onNext: onSubmit,
        ...props,
      })}
    </div>
  )
}

export default PhoneNumberScreen
